import type { ConnectorResponse } from "../../../types/crm";
import "./ConnectorCard.css";

interface ConnectorCardProps {
  platform: string;
  connector?: ConnectorResponse;
  onConnect?: (platform: string) => void;
  connecting?: boolean;
  onDisconnect?: (id: string) => void;
  disconnecting?: boolean;
}

const PLATFORM_LABELS: Record<string, string> = {
  Slack: "Slack",
  Gmail: "Gmail",
  Twitter: "Twitter / X",
  Quo: "Quo",
  BlueBubbles: "BlueBubbles iMessage",
};

const STATUS_LABELS: Record<string, string> = {
  Active: "Connected",
  Error: "Error",
  Pending: "Pending",
};

export const ConnectorCard = ({
  platform,
  connector,
  onConnect,
  connecting,
  onDisconnect,
  disconnecting,
}: ConnectorCardProps) => {
  const label = PLATFORM_LABELS[platform] ?? platform;
  const isConnected = !!connector && connector.status === "Active";
  const hasError = !!connector && connector.status === "Error";

  // BB-specific: surface "stale" when the heartbeat hasn't ticked in >15 min on an Active connector.
  // No alarm — just signals the user that the next health check is overdue.
  const isStale =
    platform === "BlueBubbles" &&
    isConnected &&
    connector?.lastHealthCheckAt != null &&
    Date.now() - new Date(connector.lastHealthCheckAt).getTime() > 15 * 60 * 1000;

  const statusLabel = !connector
    ? "Not connected"
    : isStale
    ? "Active (stale)"
    : (STATUS_LABELS[connector.status] ?? connector.status);

  return (
    <div className={`connector-card ${hasError ? "connector-card--error" : ""}`}>
      <div className="connector-card__info">
        <span className="connector-card__name">{label}</span>
        <span
          className={`connector-card__status ${
            isStale
              ? "connector-card__status--stale"
              : isConnected
              ? "connector-card__status--active"
              : hasError
              ? "connector-card__status--error"
              : "connector-card__status--inactive"
          }`}
          title={connector?.errorMessage ?? undefined}
        >
          {statusLabel}
        </span>
        {connector?.externalAccountId && (
          <span className="connector-card__account">
            {connector.externalAccountId}
          </span>
        )}
        {hasError && connector?.errorMessage && (
          <span className="connector-card__error-msg">{connector.errorMessage}</span>
        )}
        {connector?.lastSyncedAt && (
          <span className="connector-card__sync">
            Last synced {new Date(connector.lastSyncedAt).toLocaleString()}
          </span>
        )}
        {platform === "BlueBubbles" && connector?.lastHealthCheckAt && (
          <span className="connector-card__sync">
            Last health check {new Date(connector.lastHealthCheckAt).toLocaleString()}
          </span>
        )}
      </div>

      <div className="connector-card__actions">
        {isConnected || hasError ? (
          <button
            className="connector-card__btn connector-card__btn--disconnect"
            onClick={() => connector && onDisconnect?.(connector.id)}
            disabled={disconnecting}
          >
            {disconnecting ? "Disconnecting..." : "Disconnect"}
          </button>
        ) : onConnect ? (
          <button
            className="connector-card__btn connector-card__btn--connect"
            onClick={() => onConnect(platform)}
            disabled={connecting}
          >
            {connecting ? "Connecting..." : "Connect"}
          </button>
        ) : null}
      </div>
    </div>
  );
};
